/** Today: the session cockpit (U016-U020; V08-A10). */
import { decodeTodayRow, type IncidentRow } from "../../contracts/views.ts";
import {
  badge,
  definitions,
  instantCell,
  metric,
  renderFreshnessBar,
} from "../../app/components.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import { formatExchangeDate, formatPaise } from "../../app/format.ts";
import { acknowledge, viewIncident } from "../../app/latches.ts";
import { hashOf } from "../../app/router.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { handleOf, makeFrame } from "../frame.ts";
import { entryBlock, opportunitiesState, reduceToday, type TodayModel } from "./model.ts";

function panel(title: string, id: string, ...content: Child[]): HTMLElement {
  return h(
    "section",
    { class: "panel", attrs: { "aria-labelledby": id } },
    h("h2", { attrs: { id } }, title),
    ...content,
  );
}

function readinessPanel(model: TodayModel): HTMLElement {
  const items = model.readiness.map((row) =>
    h(
      "li",
      { class: `readiness readiness-${row.state.toLowerCase()}` },
      badge(row.state, row.state === "READY" ? "ok" : row.state === "BLOCKED" ? "bad" : "warn"),
      h("span", { class: "readiness-label" }, ` ${row.label} `),
      h("span", { class: "scope" }, `scope ${row.scope}`),
      row.reason === null ? null : h("span", { class: "reason" }, ` — ${row.reason}`),
      h("span", { class: "checked" }, " checked "),
      instantCell(row.checkedAt),
      h("a", { attrs: { href: hashOf("data", "evidence", row.evidenceRef) } }, " evidence"),
    ),
  );
  const blockers = model.blockers.map((blocker) =>
    h(
      "li",
      { attrs: { "data-blocker": blocker.item } },
      `${blocker.label}: ${blocker.state} (scope ${blocker.scope}) — ${blocker.reason}`,
    ),
  );
  return panel(
    "Pre-session readiness",
    "today-readiness",
    blockers.length === 0
      ? h("p", { class: "all-clear" }, "Every prerequisite is ready.")
      : h(
          "div",
          { class: "blockers", attrs: { role: "group", "aria-label": "Scoped blockers" } },
          h("h3", {}, "Scoped blockers"),
          h("ul", {}, ...blockers),
        ),
    h("ul", { class: "readiness-list" }, ...items),
  );
}

function phasePanel(model: TodayModel): HTMLElement {
  const { phase } = model;
  return panel(
    "Session phase and deadlines",
    "today-phase",
    phase === null
      ? renderStateBanner({ kind: "unavailable", reason: "no session phase was reported" })
      : definitions([
          ["Phase", phase.phase],
          ["Target-flat policy", phase.policy],
          ...phase.deadlines.map((deadline) => [deadline.name, instantCell(deadline.at)] as const),
        ]),
  );
}

function opportunitiesPanel(model: TodayModel): HTMLElement {
  const state = opportunitiesState(model);
  const rows = model.opportunities.map((row) =>
    h(
      "li",
      { class: `opportunity opportunity-${row.status.toLowerCase()}` },
      badge(
        row.status === "ACTIONABLE" ? "ACTIONABLE" : "NO_TRADE",
        row.status === "ACTIONABLE" ? "ok" : "info",
      ),
      ` ${row.listing} `,
      row.side === null ? metric(null) : h("span", {}, row.side),
      row.reason === null ? null : h("span", { class: "reason" }, ` — ${row.reason}`),
      row.missingInputs.length === 0
        ? null
        : h("span", { class: "missing" }, ` (missing: ${row.missingInputs.join(", ")})`),
      row.planId === null
        ? null
        : h("a", { attrs: { href: hashOf("opportunities", "board") } }, " open board"),
    ),
  );
  return panel(
    "Opportunities",
    "today-opportunities",
    state.kind === "ready" ? null : renderStateBanner(state),
    h("ul", {}, ...rows),
  );
}

function exposurePanel(model: TodayModel): HTMLElement {
  const { exposure } = model;
  return panel(
    "Account exposure",
    "today-exposure",
    exposure === null
      ? renderStateBanner({ kind: "unavailable", reason: "no exposure snapshot was reported" })
      : h(
          "div",
          {},
          definitions([
            ["Gross exposure", formatPaise(exposure.grossPaise)],
            ["Pending reservations", formatPaise(exposure.reservedPaise)],
            ["Unresolved states", String(exposure.unresolvedCount)],
          ]),
          h(
            "ul",
            { class: "exits", attrs: { "aria-label": "Approaching exits" } },
            ...exposure.approachingExits.map((exit) =>
              h("li", {}, `${exit.listing} exit at `, instantCell(exit.exitAt)),
            ),
          ),
          h(
            "a",
            { attrs: { href: hashOf("portfolio", "evidence", exposure.evidenceRef) } },
            "Responsible evidence",
          ),
        ),
  );
}

function incidentItem(
  ctx: ViewContext,
  row: IncidentRow,
  local: ReadonlySet<string>,
  rerender: () => void,
): HTMLElement {
  const shown = viewIncident(row, local);
  const button = h(
    "button",
    {
      class: "ack",
      attrs: {
        type: "button",
        "aria-label": `Acknowledge incident ${row.incidentId} (does not release the safety latch)`,
      },
      on: {
        click: () => {
          acknowledge(ctx.acknowledgements, row.incidentId);
          rerender();
        },
      },
    },
    shown.acknowledged ? "Acknowledged" : "Acknowledge",
  );
  return h(
    "li",
    { class: "incident", attrs: { "data-incident": row.incidentId } },
    badge(row.severity, row.severity === "LOW" || row.severity === "MEDIUM" ? "warn" : "bad"),
    ` ${row.title} — owner ${row.owner ?? "unassigned"}, onset `,
    instantCell(row.onset),
    h("a", { attrs: { href: hashOf("operations", "runbook", row.runbookRef) } }, " runbook"),
    h(
      "p",
      { class: "latch", attrs: { "data-latch": row.safetyLatch } },
      `Safety latch: ${row.safetyLatch}. ${shown.acknowledged ? "Acknowledged" : "Not acknowledged"}; acknowledgement does not clear a safety latch.`,
    ),
    button,
  );
}

/** Mount the Today cockpit. */
export function mountToday(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("today", "Today");
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const draw = (current: TodayModel): void => {
    const block = entryBlock(current);
    setChildren(frame.body, [
      block.blocked
        ? h(
            "div",
            { class: "entry-block", attrs: { role: "alert", "data-entry-block": "true" } },
            h("strong", {}, "New entries are blocked. "),
            block.reasons.map((r) => `${r.reason} (scope ${r.scope})`).join("; "),
          )
        : null,
      current.strategy === null
        ? null
        : definitions([
            ["Strategy version", current.strategy.version],
            ["Exchange date", formatExchangeDate(current.strategy.exchangeDate)],
            ["Calendar version", current.strategy.calendarVersion],
          ]),
      readinessPanel(current),
      phasePanel(current),
      opportunitiesPanel(current),
      exposurePanel(current),
      panel(
        "Incidents and tasks",
        "today-incidents",
        current.incidents.length === 0
          ? renderStateBanner({ kind: "no-observations" })
          : h(
              "ul",
              {},
              ...current.incidents.map((row) =>
                incidentItem(ctx, row, ctx.acknowledgements.get(), () => {
                  draw(current);
                }),
              ),
            ),
      ),
    ]);
  };
  void ctx.client
    .read("today", decodeTodayRow, { accountId: ctx.accountId })
    .then((answer) => {
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      frame.banner.replaceChildren(
        ...(stateOfEnvelope(answer).kind === "ready"
          ? []
          : [renderStateBanner(stateOfEnvelope(answer))]),
      );
      frame.freshness.replaceChildren(
        renderFreshnessBar(answer, { streamSequence: ctx.streamSequence.get() }),
      );
      draw(reduceToday(answer.page.rows));
    })
    .catch((error: unknown) => {
      frame.banner.replaceChildren(renderStateBanner(stateOfError(error)));
    });
  return handleOf(frame, ctx);
}
